import { Avatar, Box, Button, DropdownMenu, GlobalNavigation, Menu } from '@acast/decibel';
import {
  AngleDownLightGlyph,
  CogRegularGlyph,
  FileAltLightGlyph,
  SignOutLightGlyph,
} from '@acast/decibel-glyphs';
import T from 'prop-types';
import React from 'react';
import { generateUrl, hasAccessTo } from './userAppPermissions';

const TopNavigationBar = ({
  selected,
  dashboardUrl,
  createUrl,
  insightsUrl,
  financeUrl,
  userImageUrl,
  profileAppUrl,
  privacyPolicyUrl,
  logoutUrl,
  customLinks,
  customUserMenuLinks,
  anonymous,
  permissions,
  roles,
  rolesApiPermissions,
  token,
  encodedSession,
  isAuth0,
}) => {
  const renderItem = (type, label, href) => {
    if (!href) return undefined;
    if (!hasAccessTo(label, permissions, roles, rolesApiPermissions)) return undefined;
    const urlWithAccess = generateUrl(label, href, isAuth0, token, encodedSession);
    return (
      <GlobalNavigation.Item
        key={type}
        selected={selected === type}
        href={urlWithAccess}
        data-test-id={`TopNavigationBar__navItem TopNavigationBar__navItem--${type}`}
      >
        {label}
      </GlobalNavigation.Item>
    );
  };

  return (
    <GlobalNavigation>
      <GlobalNavigation.Column align="left">
        {renderItem('dashboard', 'Dashboard', dashboardUrl)}
        {renderItem('create', 'Create', createUrl)}
        {renderItem('insights', 'Insights', insightsUrl)}
        {renderItem('payout-information', 'Payout Information', financeUrl)}
        {customLinks.map(({ label, url, type }) => renderItem(type, label, url))}
      </GlobalNavigation.Column>

      <GlobalNavigation.Column align="right" stretch>
        {!anonymous && (
          <DropdownMenu
            toggle={
              <Button variant="tertiary" glyphEnd={AngleDownLightGlyph} aria-label="user menu">
                <Box alignItems="center">
                  <Avatar title="User menu" size="xsmall" src={userImageUrl.original} />
                </Box>
              </Button>
            }
          >
            <DropdownMenu.Menu>
              <DropdownMenu.Item glyph={CogRegularGlyph} href={profileAppUrl}>
                Profile settings
              </DropdownMenu.Item>
              <DropdownMenu.Item glyph={FileAltLightGlyph} href={privacyPolicyUrl}>
                Privacy policy
              </DropdownMenu.Item>
              {customUserMenuLinks &&
                customUserMenuLinks.map(({ label, url, onClick, glyph }) => (
                  <DropdownMenu.Item key={label} href={url} onClick={onClick} glyph={glyph}>
                    {label}
                  </DropdownMenu.Item>
                ))}

              {logoutUrl && (
                <>
                  <Menu.Separator as="li" />
                  <DropdownMenu.Item
                    glyph={SignOutLightGlyph}
                    data-test-id="TopNavigationBar__logoutItem"
                    href={typeof logoutUrl === 'string' ? logoutUrl : undefined}
                    onClick={typeof logoutUrl === 'function' ? logoutUrl : undefined}
                  >
                    Log out
                  </DropdownMenu.Item>
                </>
              )}
            </DropdownMenu.Menu>
          </DropdownMenu>
        )}
      </GlobalNavigation.Column>
    </GlobalNavigation>
  );
};

TopNavigationBar.propTypes = {
  /** dashboard | create | insights | finance | customLinks type */
  selected: T.string.isRequired,
  dashboardUrl: T.string.isRequired,
  createUrl: T.string,
  insightsUrl: T.string,
  financeUrl: T.string,
  userImageUrl: T.oneOfType([T.string, T.shape({ original: T.string })]),
  imageCdnUrl: T.string,
  profileAppUrl: T.string.isRequired,
  privacyPolicyUrl: T.string.isRequired,
  logoutUrl: T.oneOfType([T.string, T.func]),
  customLinks: T.arrayOf(
    T.shape({
      type: T.string.isRequired,
      label: T.string.isRequired,
      url: T.string,
    })
  ),
  customUserMenuLinks: T.arrayOf(
    T.shape({
      label: T.string.isRequired,
      url: T.string,
      onClick: T.func,
    })
  ),
  anonymous: T.bool,
  permissions: T.arrayOf(
    T.shape({
      resource: T.string.isRequired,
      role: T.string.isRequired,
    })
  ).isRequired,
  rolesApiPermissions: T.arrayOf(T.shape({ app: T.string.isRequired })),
  roles: T.arrayOf(T.string.isRequired).isRequired,
  token: T.string.isRequired,
  encodedSession: T.string.isRequired,
  isAuth0: T.bool,
};

TopNavigationBar.defaultProps = {
  createUrl: undefined,
  insightsUrl: undefined,
  financeUrl: undefined,
  userImageUrl: undefined,
  imageCdnUrl: undefined,
  logoutUrl: undefined,
  customLinks: [],
  customUserMenuLinks: undefined,
  anonymous: false,
  isAuth0: false,
};

export default TopNavigationBar;
